import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { uploadFile } from "@/lib/uploads";
import { toast } from "@/components/Toast";
import { ArrowLeft, ScanLine, Check, AlertTriangle, X, Camera, Keyboard, Radar, Satellite, WifiOff, RefreshCw, ImagePlus } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { usePatrolTracking } from "@/hooks/usePatrolTracking";
import { VERIFICATION_MODE_LABEL } from "@/lib/i18n";

const queueKey = (id) => `invict_offline_scans_${id}`;
const loadQueue = (id) => { try { return JSON.parse(localStorage.getItem(queueKey(id)) || "[]"); } catch { return []; } };
const saveQueue = (id, q) => localStorage.setItem(queueKey(id), JSON.stringify(q));

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const base64ToFile = (dataUrl, filename, mime) => {
  const [, b64] = dataUrl.split(",");
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], filename, { type: mime });
};

export default function VigiladorPatrolActive() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [orderConfirm, setOrderConfirm] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [gpsBusy, setGpsBusy] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState(() => loadQueue(id));
  const [syncing, setSyncing] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const capturedPhotoRef = useRef(null);
  const photoInputRef = useRef(null);
  const scannerRef = useRef(null);
  const scannerIdRef = useRef(`qr-reader-${Math.random().toString(36).slice(2, 8)}`);
  const trackingStatus = usePatrolTracking(id, p?.status === "en_curso");
  const syncingRef = useRef(false);

  const load = async () => {
    try {
      // Start the patrol if pendiente. /start returns full detail incl. scans.
      const { data } = await api.post(`/patrols/${id}/start`);
      setP(data);
    } catch (e) {
      if (!e.response) return; // sin conexión al abrir la ronda: seguimos con lo que ya haya en memoria/queue
      toast.error(formatApiError(e));
      nav("/vigilador");
    }
  };
  useEffect(() => { load(); }, [id]);

  // ---- Modo offline: detección de conexión + sincronización de la cola local ----
  const syncQueue = async () => {
    if (syncingRef.current) return;
    let q = loadQueue(id);
    if (!q.length) return;
    syncingRef.current = true;
    setSyncing(true);
    let syncedCount = 0;
    while (q.length) {
      const item = q[0];
      try {
        let attachments = [];
        if (item.photo) {
          const file = base64ToFile(item.photo.base64, item.photo.name || "evidencia.jpg", item.photo.mime);
          const up = await uploadFile(file);
          attachments = [{ url: up.url, content_type: up.content_type, kind: up.kind, size: up.size }];
        }
        const body = {
          ...item.payload,
          latitude: item.latitude, longitude: item.longitude, accuracy_meters: item.accuracy_meters,
          scanned_at: item.scanned_at, offline_sync: true, attachments,
        };
        let { data } = await api.post(`/patrols/${id}/scan`, body);
        if (data.out_of_order) {
          ({ data } = await api.post(`/patrols/${id}/scan`, { ...body, force_anomaly: true }));
        }
        q = q.slice(1);
        saveQueue(id, q);
        setQueue(q);
        syncedCount++;
      } catch (e2) {
        break; // seguimos sin conexión o error real: dejamos el resto en cola y reintentamos después
      }
    }
    syncingRef.current = false;
    setSyncing(false);
    if (syncedCount > 0) {
      toast.success(`${syncedCount} escaneo(s) pendiente(s) sincronizado(s)`);
      await load();
    }
  };

  useEffect(() => {
    const onOnline = () => { setIsOnline(true); syncQueue(); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    if (navigator.onLine) syncQueue();
    const iv = setInterval(() => { if (navigator.onLine) syncQueue(); }, 20000);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => () => { stopScanner(); }, []);

  const beginScan = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const html5Qr = new Html5Qrcode(scannerIdRef.current);
        scannerRef.current = html5Qr;
        await html5Qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => onDecoded(decodedText),
          () => {}
        );
      } catch (e) {
        toast.error("No pudimos abrir la cámara. Verificá permisos.");
        setScanning(false);
      }
    }, 100);
  };

  const onDecoded = async (token) => {
    if (busy) return;
    setBusy(true);
    await stopScanner();
    await sendScan({ qr_token: token }, false, next);
    setBusy(false);
  };

  // Intenta obtener GPS con timeout corto; nunca rechaza (subsuelos sin señal -> null)
  const getPosition = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude, acc: pos.coords.accuracy }),
      () => resolve(null),
      { timeout: 10000, enableHighAccuracy: true }
    );
  });

  const enqueueOffline = async (payload, pointRef, coords) => {
    const item = {
      localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      pointId: pointRef?.id,
      pointName: pointRef?.name || "punto",
      payload,
      scanned_at: new Date().toISOString(),
      latitude: coords?.lat ?? null,
      longitude: coords?.lon ?? null,
      accuracy_meters: coords?.acc ?? null,
      photo: capturedPhotoRef.current ? { ...capturedPhotoRef.current } : null,
    };
    const q = [...loadQueue(id), item];
    saveQueue(id, q);
    setQueue(q);
    clearPhoto();
    toast.success(`"${item.pointName}" registrado sin conexión — se enviará automáticamente`);
  };

  const sendScan = async (payload, force_anomaly, pointRef) => {
    const coords = await getPosition();
    if (!isOnline || !navigator.onLine) {
      await enqueueOffline({ ...payload, force_anomaly }, pointRef, coords);
      return;
    }
    try {
      let attachments = [];
      if (capturedPhotoRef.current) {
        const file = base64ToFile(capturedPhotoRef.current.base64, capturedPhotoRef.current.name, capturedPhotoRef.current.mime);
        const up = await uploadFile(file);
        attachments = [{ url: up.url, content_type: up.content_type, kind: up.kind, size: up.size }];
      }
      const { data } = await api.post(`/patrols/${id}/scan`, {
        ...payload, latitude: coords?.lat, longitude: coords?.lon,
        accuracy_meters: coords?.acc, force_anomaly, attachments,
      });
      clearPhoto();
      if (data.out_of_order) {
        setOrderConfirm({ payload, ...data });
      } else {
        toast.success(`Punto registrado: ${data.scan.patrol_point_name}`);
        await load();
        if (data.points_completed >= data.total_points) {
          setTimeout(() => toast.success("¡Ronda completada!"), 200);
        }
      }
    } catch (e) {
      if (!e.response) {
        // Falla de red real (aunque el navegador diga "online") -> igual la guardamos en cola
        setIsOnline(false);
        await enqueueOffline({ ...payload, force_anomaly }, pointRef, coords);
        return;
      }
      toast.error(formatApiError(e));
    }
  };

  const confirmAnomaly = async () => {
    if (!orderConfirm) return;
    setBusy(true);
    await sendScan(orderConfirm.payload, true, next);
    setOrderConfirm(null);
    setBusy(false);
  };

  const submitManual = async () => {
    if (!manualToken.trim()) return toast.error("Ingresá un código");
    setBusy(true); setManualOpen(false);
    await sendScan({ qr_token: manualToken.trim() }, false, next);
    setManualToken(""); setBusy(false);
  };

  const markByGps = async (pointId, pointRef) => {
    setGpsBusy(true);
    await sendScan({ point_id: pointId }, false, pointRef);
    setGpsBusy(false);
  };

  const onCapturePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const base64 = await fileToBase64(f);
      capturedPhotoRef.current = { base64, mime: f.type, name: f.name };
      setPhotoPreview(base64);
    } catch { toast.error("No pudimos leer el archivo"); }
    e.target.value = "";
  };
  const clearPhoto = () => { capturedPhotoRef.current = null; setPhotoPreview(null); };

  if (!p) return <div style={{ padding: 16 }}><div className="skel" style={{ height: 30, width: 200 }} /></div>;

  const serverCompletedIds = new Set(p.scans.map((s) => s.patrol_point_id));
  const queuedIds = new Set(queue.map((q) => q.pointId));
  const completed = p.points.filter((pt) => serverCompletedIds.has(pt.id) || queuedIds.has(pt.id));
  const remaining = p.points.filter((pt) => !serverCompletedIds.has(pt.id) && !queuedIds.has(pt.id));
  const next = remaining[0];
  const isDone = (p.status === "completada") || (remaining.length === 0 && p.points.length > 0);
  const pendingCount = queue.length;

  return (
    <div style={{ padding: "8px 12px" }}>
      <Link to="/vigilador" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Salir</Link>

      <div style={{ background: "#0A0A0A", color: "#FFFBF4", padding: "18px", borderRadius: 4, marginBottom: 14 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>{p.client_name}{p.site_name ? ` · ${p.site_name}` : ""}</div>
        <div style={{ fontSize: 28, fontWeight: 900, lineHeight: 1, marginBottom: 10 }}>
          {completed.length} <span style={{ color: "#BF3437" }}>/</span> {p.total_points}
          <span style={{ fontSize: 13, color: "rgba(255,251,244,0.5)", marginLeft: 8, fontWeight: 600 }}>puntos</span>
        </div>
        {/* Progress bar */}
        <div style={{ height: 6, background: "rgba(255,251,244,0.1)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${(completed.length / p.total_points) * 100}%`, height: "100%", background: "#BF3437", transition: "width 0.4s ease" }} />
        </div>
      </div>

      {(!isOnline || pendingCount > 0) && (
        <div data-testid="offline-banner" style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 14,
          borderRadius: 4, fontSize: 12, fontWeight: 700, background: "rgba(191,52,55,0.1)",
          color: "#BF3437", border: "1px solid rgba(191,52,55,0.3)",
        }}>
          <WifiOff size={16} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, lineHeight: 1.35 }}>
            {!isOnline ? "Sin conexión — " : ""}
            {pendingCount > 0
              ? <span data-testid="offline-pending-count">{pendingCount} escaneo(s) pendiente(s) de enviar. Se suben automáticamente al recuperar señal.</span>
              : "Trabajando sin conexión: tus escaneos se guardan localmente."}
          </div>
          {isOnline && pendingCount > 0 && (
            <button type="button" onClick={syncQueue} disabled={syncing} data-testid="offline-sync-now" style={{ background: "#BF3437", color: "#fff", border: "none", borderRadius: 3, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <RefreshCw size={12} className={syncing ? "spin" : ""} /> {syncing ? "Enviando..." : "Sincronizar"}
            </button>
          )}
        </div>
      )}

      {p.status === "en_curso" && (
        <div data-testid="gps-tracking-indicator" style={{
          display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", marginBottom: 14,
          borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
          background: trackingStatus === "tracking" ? "rgba(22,163,74,0.1)" : "rgba(107,101,90,0.1)",
          color: trackingStatus === "tracking" ? "#16a34a" : "var(--invict-text-muted)",
          border: `1px solid ${trackingStatus === "tracking" ? "rgba(22,163,74,0.3)" : "var(--invict-bone-3)"}`,
        }}>
          {trackingStatus === "tracking" && (
            <>
              <span className="pulse-green-dot" data-testid="gps-tracking-active" />
              <Radar size={13} /> Seguimiento GPS activo durante la ronda
            </>
          )}
          {(trackingStatus === "denied" || trackingStatus === "unsupported") && (
            <span data-testid="gps-tracking-unavailable" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Satellite size={13} /> Sin señal GPS continua — el escaneo de cada QR sigue validando tu ubicación
            </span>
          )}
          {trackingStatus === "idle" && (
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Satellite size={13} /> Iniciando seguimiento GPS de la ronda...
            </span>
          )}
        </div>
      )}

      {!isDone && next && (
        <div className="card" style={{ marginBottom: 14, padding: 18, borderLeft: "4px solid #BF3437" }}>
          <div style={{ fontSize: 11, color: "var(--invict-text-muted)", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>Próximo punto</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#0A0A0A", margin: "4px 0 12px", letterSpacing: "-0.01em" }}>#{next.order} · {next.name}</div>

          {!scanning ? (
            <>
              {(next.verification_mode || "qr") !== "gps" && (
                <button className="btn-primary pulse-red" onClick={beginScan} disabled={busy} style={{ width: "100%", padding: "18px", fontSize: 14 }} data-testid="scan-start">
                  <Camera size={16} style={{ marginRight: 8 }} /> Escanear QR del punto
                </button>
              )}
              {(next.verification_mode === "gps" || next.verification_mode === "ambos") && (
                <button className="btn-primary" onClick={() => markByGps(next.id, next)} disabled={busy || gpsBusy} style={{ width: "100%", padding: "18px", fontSize: 14, marginTop: (next.verification_mode || "qr") !== "gps" ? 10 : 0, background: (next.verification_mode || "qr") !== "gps" ? "#0A0A0A" : undefined }} data-testid="mark-gps-start">
                  <Radar size={16} style={{ marginRight: 8 }} /> {gpsBusy ? "Buscando tu ubicación..." : "Marcar por cercanía GPS"}
                </button>
              )}
              {(next.verification_mode || "qr") !== "gps" && (
                <button className="btn-ghost" onClick={() => setManualOpen(true)} style={{ width: "100%", padding: "10px", marginTop: 10, fontSize: 11 }} data-testid="scan-manual">
                  <Keyboard size={12} style={{ marginRight: 6 }} /> Ingresar código manualmente
                </button>
              )}

              <input ref={photoInputRef} type="file" accept="image/*,video/*" capture="environment" style={{ display: "none" }} onChange={onCapturePhoto} data-testid="evidence-photo-input" />
              {!photoPreview ? (
                <button type="button" onClick={() => photoInputRef.current?.click()} style={{ width: "100%", padding: "10px", marginTop: 10, fontSize: 11, background: "transparent", border: "1px dashed var(--invict-bone-3)", borderRadius: 3, cursor: "pointer", color: "#0A0A0A", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }} data-testid="evidence-photo-btn">
                  <ImagePlus size={13} /> Adjuntar foto/video de evidencia (opcional, útil sin señal)
                </button>
              ) : (
                <div data-testid="evidence-photo-preview" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, padding: 8, background: "var(--invict-bone-2)", borderRadius: 3 }}>
                  {photoPreview.startsWith("data:image") ? (
                    <img src={photoPreview} alt="evidencia" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 3 }} />
                  ) : (
                    <Camera size={18} />
                  )}
                  <span style={{ fontSize: 11, flex: 1, fontWeight: 700 }}>Evidencia adjunta al próximo escaneo</span>
                  <button type="button" onClick={clearPhoto} data-testid="evidence-photo-clear" style={{ background: "none", border: "none", cursor: "pointer", color: "#BF3437" }}><X size={14} /></button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="scanner-wrap" style={{ marginBottom: 10 }}>
                <div id={scannerIdRef.current} style={{ width: "100%" }} />
              </div>
              <button className="btn-ghost" onClick={stopScanner} style={{ width: "100%", padding: "10px", fontSize: 11 }} data-testid="scan-stop">
                <X size={12} style={{ marginRight: 6 }} /> Cancelar escaneo
              </button>
            </>
          )}
        </div>
      )}

      {isDone && (
        <div className="card" style={{ marginBottom: 14, padding: 22, textAlign: "center", background: pendingCount > 0 ? "#BF3437" : "#16a34a", color: "white" }} data-testid="patrol-done">
          <Check size={48} style={{ margin: "0 auto 8px" }} />
          <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 4 }}>{pendingCount > 0 ? "¡Ronda completada! (falta sincronizar)" : "¡Ronda completada!"}</div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            {completed.length} de {p.total_points} puntos{p.anomalies > 0 ? ` · ${p.anomalies} anomalía(s)` : ""}.
            {pendingCount > 0 && <div style={{ marginTop: 4 }}>{pendingCount} escaneo(s) se van a subir solos cuando vuelva la señal.</div>}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 8 }}>Recorrido</div>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {p.points.map((pt) => {
          const scan = p.scans.find((s) => s.patrol_point_id === pt.id);
          const queuedItem = queue.find((q) => q.pointId === pt.id);
          const isCompleted = !!scan || !!queuedItem;
          return (
            <div key={pt.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", alignItems: "center", gap: 12, opacity: isCompleted ? 1 : 0.7 }} data-testid={`patrol-point-row-${pt.id}`}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: scan ? (scan.status === "ok" ? "#16a34a" : "#BF3437") : queuedItem ? "#d97706" : "var(--invict-bone-3)", color: isCompleted ? "white" : "#6B655A", display: "grid", placeItems: "center", flexShrink: 0, fontWeight: 800, fontSize: 12 }}>
                {scan ? (scan.status === "ok" ? <Check size={14}/> : <AlertTriangle size={14}/>) : queuedItem ? <WifiOff size={13}/> : pt.order}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>{pt.name} <span style={{ fontSize: 9, fontWeight: 800, color: "var(--invict-text-muted)", textTransform: "uppercase" }}>{VERIFICATION_MODE_LABEL[pt.verification_mode || "qr"]}</span></div>
                {scan && <div style={{ fontSize: 10, color: "var(--invict-text-muted)" }}>{new Date(scan.scanned_at).toLocaleTimeString("es-AR")} · {scan.gps_degraded ? "sin señal GPS" : `${scan.distance_meters}m`}{scan.offline_sync ? " · sincronizado offline" : ""}</div>}
                {queuedItem && <div style={{ fontSize: 10, color: "#d97706", fontWeight: 700 }} data-testid={`patrol-point-pending-${pt.id}`}>Pendiente de sincronizar · {new Date(queuedItem.scanned_at).toLocaleTimeString("es-AR")}{queuedItem.photo ? " · con evidencia" : ""}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {orderConfirm && (
        <div className="modal-overlay">
          <div className="modal" data-testid="order-confirm">
            <h3 style={{ color: "#BF3437" }}><AlertTriangle size={16} style={{ display: "inline", marginRight: 4 }}/> Punto fuera de orden</h3>
            <p>{orderConfirm.message}</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-ghost" style={{ flex: 1 }} onClick={() => setOrderConfirm(null)} data-testid="order-cancel">Cancelar</button>
              <button className="btn-primary" style={{ flex: 1 }} onClick={confirmAnomaly} disabled={busy} data-testid="order-confirm-btn">Registrar anomalía</button>
            </div>
          </div>
        </div>
      )}

      {manualOpen && (
        <div className="modal-overlay" onClick={() => setManualOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Ingresar código manualmente</h3>
            <p>Pegá o tipeá el código del QR (formato UUID).</p>
            <div className="field"><label>Código</label><input value={manualToken} onChange={(e) => setManualToken(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" data-testid="manual-token"/></div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-ghost" style={{ flex: 1 }} onClick={() => setManualOpen(false)}>Cancelar</button>
              <button className="btn-primary" style={{ flex: 1 }} onClick={submitManual} disabled={busy} data-testid="manual-submit">Registrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
